import type { UseQueryOptions } from "@tanstack/react-query";
import {
  apiRequest,
  useApiMutation,
  useApiQuery,
  type ApiError,
} from "@/shared/api";
import type {
  CreateFiatWithdrawalPayload,
  CreateFiatWithdrawalResponse,
  FiatDepositAddressResponse,
  FiatDepositDetailsResponse,
  FiatWithdrawalDetailsResponse,
  FiatWithdrawalFeeResponse,
  FiatTransactionsResponse,
  FiatBalancesResponse,
} from "../interfaces/fiat.interface";

type ApiQueryOptions<TData> = Omit<
  UseQueryOptions<TData, ApiError, TData, readonly unknown[]>,
  "queryKey" | "queryFn"
>;

const fiatDepositAddressRequest = {
  url: "/fiat/deposit/address",
  method: "get" as const,
};

export const getFiatDepositAddress = () =>
  apiRequest<FiatDepositAddressResponse>(fiatDepositAddressRequest);

export const useFiatDepositAddress = (
  options?: ApiQueryOptions<FiatDepositAddressResponse>
) =>
  useApiQuery<FiatDepositAddressResponse, ApiError>(
    ["fiatDepositAddress"],
    fiatDepositAddressRequest,
    options
  );

const fiatDepositDetailsRequest = (depositId: string) => ({
  url: `/fiat/deposit/${depositId}`,
  method: "get" as const,
});

export const getFiatDepositDetails = (depositId: string) =>
  apiRequest<FiatDepositDetailsResponse>(
    fiatDepositDetailsRequest(depositId.trim())
  );

export const useFiatDepositDetails = (
  depositId: string,
  options?: ApiQueryOptions<FiatDepositDetailsResponse>
) => {
  const normalizedId = depositId?.trim() ?? "";

  const mergedOptions: ApiQueryOptions<FiatDepositDetailsResponse> = {
    ...(options ?? {}),
    enabled:
      Boolean(normalizedId) &&
      (options?.enabled !== undefined ? options.enabled : true),
  };

  return useApiQuery<FiatDepositDetailsResponse, ApiError>(
    ["fiat-deposit-details", normalizedId],
    fiatDepositDetailsRequest(normalizedId || ""),
    mergedOptions
  );
};

const fiatWithdrawalDetailsRequest = (withdrawalId: string) => ({
  url: `/fiat/withdrawal/${withdrawalId}`,
  method: "get" as const,
});

export const getFiatWithdrawalDetails = (withdrawalId: string) =>
  apiRequest<FiatWithdrawalDetailsResponse>(
    fiatWithdrawalDetailsRequest(withdrawalId.trim())
  );

export const useFiatWithdrawalDetails = (
  withdrawalId: string,
  options?: ApiQueryOptions<FiatWithdrawalDetailsResponse>
) => {
  const normalizedId = withdrawalId?.trim() ?? "";

  const mergedOptions: ApiQueryOptions<FiatWithdrawalDetailsResponse> = {
    ...(options ?? {}),
    enabled:
      Boolean(normalizedId) &&
      (options?.enabled !== undefined ? options.enabled : true),
  };

  return useApiQuery<FiatWithdrawalDetailsResponse, ApiError>(
    ["fiat-withdrawal-details", normalizedId],
    fiatWithdrawalDetailsRequest(normalizedId || ""),
    mergedOptions
  );
};

const fiatWithdrawalFeeRequest = (params: {
  amount: number;
  currency: string;
}) => ({
  url: "/fiat/withdrawal/fee",
  method: "get" as const,
  params: {
    amount: params.amount,
    currency: params.currency,
  },
});

export const getFiatWithdrawalFee = (params: {
  amount: number;
  currency: string;
}) => apiRequest<FiatWithdrawalFeeResponse>(fiatWithdrawalFeeRequest(params));

export const useFiatWithdrawalFee = (
  params: { amount: number; currency: string },
  options?: ApiQueryOptions<FiatWithdrawalFeeResponse>
) => {
  const normalizedCurrency = params.currency?.trim();
  const normalizedAmount = Number.isFinite(params.amount)
    ? params.amount
    : Number.parseFloat(String(params.amount ?? 0));

  const queryKey = [
    "fiat-withdrawal-fee",
    normalizedCurrency?.toLowerCase() ?? "",
    normalizedAmount,
  ] as const;

  const mergedOptions: ApiQueryOptions<FiatWithdrawalFeeResponse> = {
    ...(options ?? {}),
    enabled:
      normalizedAmount > 0 &&
      Boolean(normalizedCurrency) &&
      (options?.enabled !== undefined ? options.enabled : true),
  };

  return useApiQuery<FiatWithdrawalFeeResponse, ApiError>(
    queryKey,
    fiatWithdrawalFeeRequest({
      amount: normalizedAmount,
      currency: normalizedCurrency ?? "",
    }),
    mergedOptions
  );
};

const createFiatWithdrawalRequest = (payload: CreateFiatWithdrawalPayload) => ({
  url: "/fiat/withdrawal",
  method: "post" as const,
  data: {
    amount: payload.amount,
    currency: payload.currency,
    receiverType: payload.receiverType,
    destinationIban: payload.destinationIban,
    transferTitle: payload.transferTitle,
    receiverFirstName: payload.receiverFirstName,
    receiverLastName: payload.receiverLastName,
  },
});

export const useCreateFiatWithdrawal = () =>
  useApiMutation<CreateFiatWithdrawalResponse, CreateFiatWithdrawalPayload>(
    createFiatWithdrawalRequest
  );

const fiatTransactionsRequest = (params: { month: number; year: number }) => ({
  url: "/fiat/transactions",
  method: "get" as const,
  params: {
    month: params.month,
    year: params.year,
  },
});

export const getFiatTransactions = (params: { month: number; year: number }) =>
  apiRequest<FiatTransactionsResponse>(fiatTransactionsRequest(params));

export const useFiatTransactions = (
  params: { month: number; year: number },
  options?: ApiQueryOptions<FiatTransactionsResponse>
) => {
  const queryKey = ["fiat-transactions", params.month, params.year] as const;

  return useApiQuery<FiatTransactionsResponse, ApiError>(
    queryKey,
    fiatTransactionsRequest(params),
    options
  );
};

const fiatBalancesRequest = {
  url: "/fiat/balances",
  method: "get" as const,
};

export const getFiatBalances = () =>
  apiRequest<FiatBalancesResponse>(fiatBalancesRequest);

export const useFiatBalances = (
  options?: ApiQueryOptions<FiatBalancesResponse>
) =>
  useApiQuery<FiatBalancesResponse, ApiError>(
    ["fiat-balances"],
    fiatBalancesRequest,
    options
  );
